import ViewPropertyDetails from "../../../interface/Watchlist/propertyDetails";
import { HouseIcon, BedIcon, BathIcon, CarIcon, FilledStar, Star } from "../../../public/assets/images";
import { formatCurrency } from "../../../utils/commonUtil";
import { useEffect, useRef, useState } from "react";
import { ApiEndPoints } from "../../../utils/endpoints";
import { httpPost } from "../../../services/httpService";
import { useDispatch, useSelector } from "react-redux";
import { removePropertyFromWatchList, getWatchListProperties } from "../../../store/slice/watchlistSlice";
import { Toast } from "primereact/toast";
import { MEMBER_KEY, Messages } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../store";
import { REIProgressSpinner } from "../../../components/ui";
import { IAnalysisSummaryResponse } from "../../../interface/analysisSummary";

interface IPropertyInformation {
  propertydata: ViewPropertyDetails;
  onClosecallback: any;
  hideCloseBtn?: boolean;
}

interface IWatchListPayloadData {
  Country: string;
  LinkIDHash: string;
  MemberId: string;
  Website: string;
  WebsiteMode: string;
}

const PropertyHeader: React.FC<IPropertyInformation> = ({ propertydata, onClosecallback, hideCloseBtn }) => {
  const [isPropertyWatchListed, setIsPropertyWatchListed] = useState<boolean>(false);
  const [isWatchlistUpdating, setIsWatchlistUpdating] = useState(false);
  let watchListResponse = useSelector((state: RootState) => state.watchList?.watchListResponse);
  const analysisSummary = useSelector((state: RootState) => state.analyser.analysisSummary as IAnalysisSummaryResponse);
  const [isDeleting, setIsDeleting] = useState(false);
  const dispatch = useDispatch();
  let watchListId = useRef<string>("");
  const toastRef = useRef<Toast>(null);
  let propertyHeaderTitle = useRef<string>("");
  const filterData = useSelector((state: RootState) => state.investar.filterValue);
  const navigation = useNavigate();

  useEffect(() => {
    if (watchListResponse.error) {
      return toastRef.current?.show({
        severity: "success",
        summary: Messages.ERROR,
        detail: Messages.UNKNOWN_ERROR,
        life: 2000,
      });
    }
    if (watchListResponse.length != 0 && watchListResponse?.WatchList?.length != 0) {
      let isWatchListed =
        watchListResponse.WatchList.find((x) => x.LinkIDHash == propertydata.PropertyDetails.LinkIDHash)?.LinkIDHash !=
        undefined
          ? true
          : false;
      watchListId.current = watchListResponse.WatchList.find(
        (x) => x.LinkIDHash == propertydata.PropertyDetails.LinkIDHash
      )?.WatchlistID;
      setIsPropertyWatchListed(isWatchListed);
      propertyHeaderTitle.current = [
        propertydata.PropertyDetails.StreetAddress,
        propertydata.PropertyDetails.Suburb,
        propertydata.PropertyDetails.StateRegion,
        propertydata.PropertyDetails.Postcode,
      ]
        .filter(Boolean)
        .join(", ");
    } else {
      setIsPropertyWatchListed(false);
    }
  }, [watchListResponse]);

  const getAddress = () => {
    let address = [
      propertydata.PropertyDetails.StreetAddress,
      propertydata.PropertyDetails.Suburb,
      propertydata.PropertyDetails.StateRegion,
      propertydata.PropertyDetails.Postcode,
    ]
      .filter(Boolean)
      .join(", ");

    if (propertydata.isFromPropertyAnalyser) {
      address = propertydata.PropertyDetails.PropertyAddress ? propertydata.PropertyDetails.PropertyAddress : address;
    }
    return address;
  };

  const onStarClick = async () => {
    if (isPropertyWatchListed) {
      setIsDeleting(true);
      await dispatch(removePropertyFromWatchList(`${Number(watchListId.current)}`));
      setIsDeleting(false);
      setIsPropertyWatchListed(!isPropertyWatchListed);
    } else {
      addToWatchList();
    }
  };

  const addToWatchList = async (openAnalyser: boolean = false) => {
    setIsWatchlistUpdating((prev) => true);
    const data: IWatchListPayloadData = {
      Country: `${propertydata.PropertyDetails.Country}`,
      LinkIDHash: `${propertydata.PropertyDetails.LinkIDHash}`,
      MemberId: `${propertydata.PropertyDetails.MemberId}`,
      Website: `${propertydata.PropertyDetails.Website}`,
      WebsiteMode: `${propertydata.PropertyDetails.WebsiteMode}`,
    };

    await httpPost(`${ApiEndPoints.watchlist}`, data)
      .then((result) => {
        if (result.status == 200) {
          setIsWatchlistUpdating((prev) => false);
          dispatch(getWatchListProperties());
          setIsPropertyWatchListed(!isPropertyWatchListed);
          if (openAnalyser) {
            navigation("/property-analyser", {
              state: {
                propertyAnalyserId: propertydata.PropertyDetails.ID,
                propertyName: propertydata.PropertyDetails.StreetAddress,
              },
            });
          }
        }
      })
      .catch((error) => {
        setIsWatchlistUpdating((prev) => false);
        return toastRef.current?.show({
          severity: "success",
          summary: Messages.ERROR,
          detail: error.messege,
          life: 2000,
        });
      });
  };

  const onFullAnalyserClick = async () => {
    if (!isPropertyWatchListed) {
      await addToWatchList(true);
    } else {
      const payload = {
        MemberId: localStorage.getItem(MEMBER_KEY),
        PropertyCountryCode: propertydata.PropertyDetails.Country,
        PropertyLinkId: propertydata.PropertyDetails.LinkIDHash,
        Website: propertydata.PropertyDetails.Website,
        WebsiteMode: propertydata.PropertyDetails.WebsiteMode,
      };

      httpPost(`${ApiEndPoints.analyser}/CheckPropertyAnalyserId`, payload)
        .then((result) => {
          if (result.status == 200 && result.data != null && result.data > 0) {
            const propertyAnalyserId = result.data;
            navigation("/property-analyser/analysis-report", {
              state: {
                propertyAnalyserId: propertyAnalyserId,
                propertyName: propertydata.PropertyDetails.StreetAddress
                  ? propertydata.PropertyDetails.StreetAddress
                  : propertyHeaderTitle.current,
                propertyAddress: getAddress(),
                countryID: propertydata.PropertyDetails.Country === "AU" ? 1 : 2,
              },
            });
          } else {
            navigation("/property-analyser", {
              state: {
                propertyAnalyserId: propertydata.PropertyDetails.ID,
                propertyName: propertydata.PropertyDetails.StreetAddress
                  ? propertydata.PropertyDetails.StreetAddress
                  : propertyHeaderTitle.current,
              },
            });
          }
        })
        .catch((error) => {
          console.log("Error fetching property analyser ID:", error);
        });
    }
  };

  const highlightWords = (text: string): string => {
    let keyWords = filterData?.Country?.StrategyTypeKeywords?.filter((kw) => kw.keyword) || [];
    let strategyKeyWords =
      filterData?.Country.Strategy &&
      filterData?.Country.Strategy.length > 0 &&
      filterData?.Country.Strategy[0].keywords &&
      filterData?.Country.Strategy[0].keywords.length > 0 &&
      filterData?.Country.Strategy[0].keywords
        .toString()
        .split(",")
        .map((s) => s.trim());

    if (
      ((keyWords && keyWords.length > 0) || (strategyKeyWords && strategyKeyWords.length > 0)) &&
      text &&
      text != ""
    ) {
      keyWords = keyWords.map((kw) => kw.keyword);
      keyWords = keyWords.concat(strategyKeyWords);

      const regex = new RegExp(`\\b(${keyWords.join("|")})\\b`, "gi");
      return text.replace(regex, (match) => (match == " " ? match : `<span class="highlight">${match}</span>`));
    }
    return text;
  };

  return (
    <>
      <div>
        {!propertydata.isFromPropertyAnalyser && (
          <div className="flex md:flex-row flex-col bg-slate-100 p-2">
            <div className="w-full md:w-1/2 text-start">
              {!isDeleting && !isWatchlistUpdating && (
                <img
                  id="wlh-icon-star"
                  data-testid="wlh-icon-star"
                  src={`${isPropertyWatchListed ? FilledStar : Star}`}
                  style={{
                    height: "20px",
                  }}
                  onClick={onStarClick}
                />
              )}
              {(isDeleting || isWatchlistUpdating) && (
                <span>
                  <REIProgressSpinner style={{ width: "20px", height: "20px" }}></REIProgressSpinner>
                </span>
              )}
              <span className="font-family_2 font-bold text-base md:text-lg align-top p-1">
                {propertydata.PropertyDetails.Suburb} {propertydata.PropertyDetails.DateDownloadedFirst}
              </span>
            </div>
            <div className="w-full md:w-1/2 text-end font-bold">
              {propertydata.PropertyDetails.Price == 0 ? "" : formatCurrency(propertydata.PropertyDetails.Price)}
              {propertydata.PropertyDetails.LocalityMedian === "(N/A % of median)"
                ? ""
                : propertydata.PropertyDetails.LocalityMedian}
            </div>
          </div>
        )}
        <div className="flex md:flex-row flex-col justify-between pl-2">
          {!propertydata.isFromPropertyAnalyser && (
            <div className="w-full md:w-1/2 my-1 text-base md:text-lg text-orange-700 font-bold">{getAddress()}</div>
          )}
          {propertydata.isFromPropertyAnalyser && (
            <div className="w-full md:w-1/2 my-1 text-base md:text-lg text-orange-700 font-bold">{getAddress()}</div>
          )}
          <div className="w-full md:w-1/4 flex flex-row mr-2">
            <div className="w-1/2 text-start content-center">
              <img src={HouseIcon} alt="Image 1" className="h-5 w-5" title="Property Type" />
              <span className="text-center text-xs md:text-sm m-1 align-top">
                {propertydata.PropertyDetails.Category}
              </span>
            </div>
            <div className="w-1/2 text-end pr-2 content-center">
              <img src={BedIcon} alt="Image 2" className="h-5 w-5" title="Number of Bedrooms" />
              <span className=" text-sm md:text-base align-top m-1">{propertydata.PropertyDetails.BedRooms}</span>
              <img src={BathIcon} alt="Image 1" className="h-5 w-5" title="Number of Bathrooms" />
              <span className="text-center text-sm md:text-base align-top m-1">
                {propertydata.PropertyDetails.BathRooms}
              </span>
              <img src={CarIcon} alt="Image 3" className="h-5 w-5" title="Number of parking spaces" />
              <span className=" text-sm md:text-base align-top m-1">{propertydata.PropertyDetails.Carport}</span>
            </div>
          </div>
          <div className="w-full md:w-1/4 flex flex-row content-center">
            <div className="w-1/2 text-start content-center text-xs md:text-sm">
              {`Land size: ${
                propertydata.PropertyDetails.LandArea == 0
                  ? "N/A"
                  : `${
                      propertydata.PropertyDetails.LandArea.toString().includes("m²")
                        ? propertydata.PropertyDetails.LandArea
                        : `${propertydata.PropertyDetails.LandArea} m²`
                    }`
              }`}
            </div>
            {propertydata.isFromPropertyAnalyser && (
              <div className="w-1/2 text-start content-center text-xs md:text-sm">
                added {propertydata.PropertyDetails.DataAdded.split(" ")[0]}
              </div>
            )}
            {!propertydata.isFromPropertyAnalyser && (
              <div className="w-1/2 text-end content-center">
                <span
                  id="wlh-lnk-analysefull"
                  data-testid="wlh-lnk-analysefull"
                  className="text-xs md:text-sm mr-1 pt-2 cursor-pointer underline text-blue-500"
                  onClick={() => onFullAnalyserClick()}
                >
                  Analyse in full
                </span>
              </div>
            )}
          </div>
        </div>
        {!propertydata.isFromPropertyAnalyser && (
          <div className="font-bold pl-2 text-sky-600 pb-2">
            <span
              dangerouslySetInnerHTML={{
                __html: highlightWords(propertydata.PropertyDetails.Title.replace(/^,|,$/g, "")),
              }}
            ></span>
          </div>
        )}
      </div>
    </>
  );
};
export default PropertyHeader;
